import { useState } from "react";
import {
  AppointmentOffering,
  SelectedAddOn as WireSelectedAddOn,
} from "@Shape-Digital/kudzu-data/lib/types/actions";

import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";

import {
  AddOnsState,
  SelectedCenterService,
  SelectedOfferingSide,
} from "./types";

const toWireSelectedAddOns = (params: {
  addOnIds?: string[];
  addOnQuantities?: Record<string, number>;
}): WireSelectedAddOn[] => {
  const { addOnIds, addOnQuantities } = params;

  return (addOnIds || []).map((id) => ({
    id,
    quantity: addOnQuantities?.[id] ?? 1,
  }));
};

export const useSubmitHandler = ({
  onSuccess,
  onError,
  functionName = "editAppointmentServices",
}: {
  onSuccess: () => void;
  onError: () => void;
  functionName?: "editAppointmentServices" | "editAppointmentAddOns";
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const editAppointmentServices = useFirebaseAppFunction(functionName);

  const onSubmit = async ({
    appointmentId,
    selectedAddOns,
    selectedCenterServices,
  }: {
    appointmentId: string;
    selectedAddOns: AddOnsState;
    selectedCenterServices: (SelectedCenterService | null)[];
  }) => {
    const selectedAddonsIds: WireSelectedAddOn[] = selectedAddOns.map(
      (addOn) => ({ id: addOn.id, quantity: addOn.quantity }),
    );
    const selectedOfferings: AppointmentOffering[] = selectedCenterServices
      .filter((option) => !!option && option.groupId === "single-offering")
      .map((option) => ({
        id: option?.value || "",
        side: (option?.side || null) as SelectedOfferingSide | null,
        selectedAddOns: toWireSelectedAddOns({
          addOnIds: option?.addOnIds,
          addOnQuantities: option?.addOnQuantities,
        }),
      }));

    const selectedPackages = selectedCenterServices
      .filter((option) => !!option && option.groupId === "package")
      .map((option) => ({
        id: option?.value || "",
        selectedAddOns: toWireSelectedAddOns({
          addOnIds: option?.addOnIds,
          addOnQuantities: option?.addOnQuantities,
        }),
      }));

    try {
      setIsLoading(true);
      await editAppointmentServices({
        appointmentId,
        addOnsIds: selectedAddonsIds,
        packages: selectedPackages,
        offerings: selectedOfferings,
      });
      onSuccess();
    } catch (error) {
      onError();
    } finally {
      setIsLoading(false);
    }
  };

  return { onSubmit, isLoading };
};
